import React, { useEffect, useState } from 'react'
import { ICellRendererParams, IRowNode } from 'ag-grid-community'
import {
  GanttBackgroundCanvas,
  useLinearDatePositions,
  useOverrunPosition,
} from './GanttBackground'
import { GanttSlider } from './GanttSlider'
import { SLIDER_TRACK_CENTER } from './ganttSliderStyles'
import { ProjectPlanRow } from '../../../../../../../../pages/ProjectPlan/projectPlanOptions'
import objects from '../../../../../../../../../utils/objects'
import { GanttParameterVO } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import { MilestoneDetail } from '../../../../../../../../../domain/entity/MilestoneEntity'
import { featureFlagService } from '../../../../../../../../../utils/feature-flag'
import { getGanttBarVisualType } from '../ganttUtil'
import {
  AggregateField,
  AggregateTarget,
  WbsItemType,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import { useRowContextRegistration } from './WbsItemLink/useRowContextRegistration'

const isGanttV2 = featureFlagService.isEnabled('gantt-v2')

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; rowId: string }
>(({ children, rowId }, ref) => (
  <div
    id={`sevend-ag-cell-gantt-chart-${rowId}`}
    className="sevend-ag-cell-gantt-chart"
    ref={ref}
    style={{ pointerEvents: 'none' }}
  >
    {children}
  </div>
))

type SliderContainerProps = {
  children: React.ReactNode
  isV2: boolean
  rowHeight?: number
}

const SliderContainer = ({
  children,
  isV2,
  rowHeight,
}: SliderContainerProps) => {
  const v2Top = `calc(50% - ${SLIDER_TRACK_CENTER}px)`
  const style: React.CSSProperties = isV2
    ? {
        left: 0,
        width: '100%',
        position: 'absolute',
        top: v2Top,
      }
    : {
        left: 0,
        width: '100%',
        height: '3px',
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        top: (rowHeight || 32) / 2 - 4,
        margin: '-1px',
      }
  return <div style={style}>{children}</div>
}

// By default, the selected row of the AGGrid is the edit target.
// Set GanntEditTargetNodeSelectionProps if you want to customize the target row.
export type GanntEditTargetNodeSelectionProps = {
  getTargetNodes: (params: GanttCellRendererProps) => IRowNode[] | undefined
  rememberTargetNodes: (nodes: IRowNode[] | undefined) => void
  clearTargetNodes: () => void
}

export interface GanttCellRendererProps extends ICellRendererParams {
  displayTimeScale: CalendarDateVO[]
  timeScale: CalendarDateVO[]
  parameter: GanttParameterVO
  updateRows: (row: ProjectPlanRow[]) => void
  setParameter: (param: GanttParameterVO) => void
  editable?: boolean
  dataPath?: string
  customEditTargetNodeSelection?: GanntEditTargetNodeSelectionProps
  milestones?: MilestoneDetail[]
  isV2?: boolean
}

const CellRenderer = (props: GanttCellRendererProps) => {
  const { context } = props
  const ganttParameter: GanttParameterVO = context.ganttParameter
  const displayOptions =
    ganttParameter?.displayOptions ?? GanttParameterVO.defaultDisplayOptions()
  const aggregateField: AggregateField =
    context.aggregateField ?? AggregateField.WBS_ITEM_WORKLOAD
  const aggregateTarget: AggregateTarget =
    context.aggregateTarget ?? WbsItemType.TASK

  const ganttParams = {
    ...props,
    parameter: ganttParameter,
    displayTimeScale: context.ganttDisplayTimeScale,
    timeScale: context.ganttTimeScale,
    milestones: context.milestones,
    isV2: isGanttV2,
  }

  const prefix = props.dataPath ? `${props.dataPath}.` : ''
  const wbsItem = objects.getValue(props.data, `${prefix}wbsItem`)
  const cumulation = objects.getValue(props.data, `${prefix}cumulation`)
  const datePositions = useLinearDatePositions(ganttParams.timeScale ?? [])
  const overrun = useOverrunPosition(
    datePositions,
    wbsItem?.scheduledDate?.endDate,
    cumulation?.maxScheduledEndDate
  )

  // Track rowIndex as state so that changes (detected via modelUpdated)
  // trigger re-renders and propagate through React.memo children.
  // ag-grid mutates node.rowIndex in place without triggering React re-render.
  const [rowIndex, setRowIndex] = useState(props.node?.rowIndex ?? -1)

  useEffect(() => {
    const api = props.api
    const node = props.node
    if (!api || !node) return

    const onModelUpdated = () => {
      const idx = node.rowIndex
      if (idx !== undefined && idx !== null && idx >= 0) {
        setRowIndex(idx)
      }
    }

    api.addEventListener('modelUpdated', onModelUpdated)
    return () => api.removeEventListener('modelUpdated', onModelUpdated)
  }, [props.api, props.node])

  useRowContextRegistration({
    rowIndex,
    wbsItem,
    projectPlanUuid: props.data?.uuid,
  })

  if (!wbsItem || !ganttParams.displayTimeScale || !ganttParams.timeScale) {
    return <></>
  }

  const hasChildren =
    (props.node.allLeafChildren && props.node.allLeafChildren.length > 0) ||
    false
  const visualType = getGanttBarVisualType(wbsItem?.wbsItemType, hasChildren)

  return (
    <>
      <ChartContainer rowId={props.node.id || ''}>
        <GanttBackgroundCanvas
          {...ganttParams}
          rowIndex={rowIndex}
          visualType={visualType}
        />
      </ChartContainer>
      <SliderContainer
        isV2={isGanttV2}
        rowHeight={props.node.rowHeight ?? undefined}
      >
        <GanttSlider
          {...ganttParams}
          editable={props.editable !== false}
          displayOptions={displayOptions}
          aggregateField={aggregateField}
          aggregateTarget={aggregateTarget}
          cumulation={cumulation}
          overrunLeft={overrun?.left}
          overrunWidth={overrun?.width}
          hasChildren={hasChildren}
          visualType={visualType}
          wbsItemForDisplay={{
            displayName: wbsItem.displayName,
            status: wbsItem.status,
            responsible: wbsItem.responsible,
            wbsItemType: wbsItem.wbsItemType,
          }}
        />
      </SliderContainer>
    </>
  )
}

export default CellRenderer
